/**
 * "Save and publish" on an edit draft (POST /api/editorial/pieces/{id}/save-in-place).
 *
 * A save that would drop something (an image, video, table, gallery, locked block, link or the
 * featured photo) is refused with 409 would_lose and the list. The editor reads it under
 * "Saving would lose:" and either confirms ("Remove them and save": the same save, with every key
 * acknowledged) or keeps editing.
 *
 * `what` and `where` quote the article's own text and URLs: they are only ever shown as text
 * (React text nodes), never as HTML.
 */
import { ApiError } from '../../api/client'
import { describeError } from '../../lib/describeError'
import { errorCode } from '../api'
import { WEB_UNCHECKED_MESSAGE } from '../refreshPages'

export type Loss = { kind: string; key: string; what: string; where: string }

export const LOSS_HEADING = 'Saving would lose:'

/** One line per loss: "<what>, <where>". */
export const lossLines = (losses: Loss[]): string[] => losses.map((l) => `${l.what}, ${l.where}`)

/** Every loss confirmed: the keys to send back as `acknowledged`. */
export const acknowledgeAll = (losses: Loss[]): string[] => losses.map((l) => l.key)

const isLoss = (x: unknown): x is Loss => {
  if (!x || typeof x !== 'object') return false
  const l = x as Record<string, unknown>
  return typeof l.kind === 'string' && typeof l.key === 'string' && typeof l.what === 'string' && typeof l.where === 'string'
}

/** The losses of a would_lose refusal, or null for any other error. */
export function refusalLosses(err: unknown): Loss[] | null {
  if (!(err instanceof ApiError) || typeof err.detail !== 'object' || err.detail === null) return null
  const d = (err.detail as { detail?: unknown }).detail
  if (!d || typeof d !== 'object' || (d as { code?: unknown }).code !== 'would_lose') return null
  const losses = (d as { losses?: unknown }).losses
  const ok = Array.isArray(losses) ? losses.filter(isLoss).map((l) => ({ kind: l.kind, key: l.key, what: l.what, where: l.where })) : []
  // Nothing readable to confirm: an ordinary error, never an empty dialog that resends [] forever.
  return ok.length ? ok : null
}

/** The notice on an edit draft whose article has a live copy on the other site (DraftDetail.edit_twin_site):
 *  saving changes only this copy. */
export function twinNotice(site: 'members' | 'free'): string {
  const other = site === 'members' ? "members' copy" : 'free copy'
  return `This piece also has a ${other} on the other site. Saving here changes only this copy, so edit the ${other} too if the correction belongs there.`
}

/** What a refused Save and publish shows, and whether the piece must be read again
 *  (stale_article: it is now stale; web_unchecked: the ticks shown are not the stored ones). */
export function saveRefusal(err: unknown): { message: string; reload: boolean } {
  const code = errorCode(err)
  return {
    message: code === 'web_unchecked' ? WEB_UNCHECKED_MESSAGE : describeError(err),
    reload: code === 'stale_article' || code === 'web_unchecked',
  }
}
